import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";

// Device LOCAL time of day. Boundaries: morning 05:00-11:59, afternoon 12:00-16:59,
// evening 17:00-04:59.
export function timeOfDay(d = new Date()) {
  const h = d.getHours();
  if (h >= 5 && h <= 11) return "morning";
  if (h >= 12 && h <= 16) return "afternoon";
  return "evening";
}

// Build "Dr <name>" from the user's profile, de-duplicating an existing Dr/Doctor title.
// Returns null when there is no usable name (never email/id).
export function greetingName(user) {
  const raw = (user?.preferred_name || user?.name || "").trim();
  if (!raw) return null;
  const base = raw.replace(/^\s*(dr\.?|doctor)\s+/i, "").trim();
  if (!base) return null;
  return `Dr ${base}`;
}

export function formatGreeting(user, d = new Date()) {
  const part = timeOfDay(d);
  const label = part === "morning" ? "Good morning" : part === "afternoon" ? "Good afternoon" : "Good evening";
  const name = greetingName(user);
  return name ? `${label}, ${name} \u{1F44B}` : `${label} \u{1F44B}`;
}

// Recomputes on mount and whenever the app is resumed/refocused (native resume + web
// visibilitychange), so the greeting stays correct when the app is reopened.
export function useGreeting(user) {
  const compute = useCallback(() => formatGreeting(user), [user]);
  const [greeting, setGreeting] = useState(compute);

  useEffect(() => {
    setGreeting(formatGreeting(user));
    const onVis = () => {
      if (document.visibilityState === "visible") setGreeting(formatGreeting(user));
    };
    document.addEventListener("visibilitychange", onVis);

    let remove;
    if (Capacitor.isNativePlatform()) {
      import("@capacitor/app")
        .then(({ App }) =>
          App.addListener("resume", () => setGreeting(formatGreeting(user))).then((h) => {
            remove = () => h.remove();
          })
        )
        .catch(() => {});
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (remove) remove();
    };
  }, [user, compute]);

  return greeting;
}
